"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export function useRovingFocus<T extends HTMLElement = HTMLElement>(options?: {
  resetOnBlur?: boolean;
  // Which item is the entry tab stop / reset target (defaults to the first).
  homeIndex?: number;
}) {
  const resetOnBlur = options?.resetOnBlur ?? true;
  const homeIndex = options?.homeIndex ?? 0;
  const ref = useRef<T>(null);
  const [active, setActive] = useState(homeIndex);

  const items = useCallback(
    () => Array.from(ref.current?.querySelectorAll<HTMLElement>("[data-roving]") ?? []),
    [],
  );

  // The lone tab stop; follows homeIndex while unfocused.
  useEffect(() => {
    const els = items();
    if (els.length === 0) return;
    const focused = ref.current?.contains(document.activeElement) ?? false;
    const target = focused || !resetOnBlur ? active : homeIndex;
    const idx = Math.min(Math.max(target, 0), els.length - 1);
    els.forEach((el, i) => {
      el.tabIndex = i === idx ? 0 : -1;
    });
  });

  const move = useCallback(
    (to: number) => {
      const els = items();
      if (els.length === 0) return;
      const next = Math.max(0, Math.min(to, els.length - 1));
      setActive(next);
      els[next]?.focus();
    },
    [items],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const els = items();
      if (els.length === 0) return;
      const current = els.indexOf(document.activeElement as HTMLElement);
      const from = current < 0 ? active : current;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault();
          move(from + 1);
          break;
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          move(from - 1);
          break;
        case "Home":
          e.preventDefault();
          move(0);
          break;
        case "End":
          e.preventDefault();
          move(els.length - 1);
          break;
      }
    },
    [active, items, move],
  );

  const onFocus = useCallback(
    (e: React.FocusEvent) => {
      const i = items().indexOf(e.target as HTMLElement);
      if (i >= 0) setActive(i);
    },
    [items],
  );

  const onBlur = useCallback(
    (e: React.FocusEvent) => {
      if (resetOnBlur && !ref.current?.contains(e.relatedTarget as Node)) setActive(homeIndex);
    },
    [resetOnBlur, homeIndex],
  );

  const focusFirst = useCallback(() => {
    setActive(0);
    items()[0]?.focus();
  }, [items]);

  return { ref, focusFirst, rovingProps: { onKeyDown, onFocus, onBlur } };
}
